import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const steps = [
  {
    id: 1,
    title: "System Initialization",
    desc: "Deploy the EduOps core to your institution. Configure campus-wide settings and department hierarchies.",
    icon: "FiServer",
    status: "ready"
  },
  {
    id: 2,
    title: "Student Synchronization",
    desc: "Import student rosters via secure upload or direct API sync. Automated validation ensures zero data collisions.",
    icon: "FiUsers",
    status: "ready"
  },
  {
    id: 3,
    title: "Dynamic Scheduling",
    desc: "Assign teachers to rooms and subjects. Our AI-assisted scheduler prevents double-bookings and optimizes room usage.",
    icon: "FiCalendar",
    status: "ready"
  },
  {
    id: 4,
    title: "Live Attendance Tracking",
    desc: "Faculty mark attendance in real-time. Data instantly flows to parent notifications and administrative reports.",
    icon: "FiCheckSquare",
    status: "active"
  },
  {
    id: 5,
    title: "Resource Dispatch",
    desc: "Manage supply requests from classrooms. Track inventory levels and approve staff requisitions in one click.",
    icon: "FiBox",
    status: "pending"
  },
  {
    id: 6,
    title: "Parent Portal Sync",
    desc: "Automated updates are pushed to the Parent Mobile App, ensuring families stay informed on academic progress.",
    icon: "FiSmartphone",
    status: "pending"
  },
  {
    id: 7,
    title: "Intelligence Reporting",
    desc: "Generate weekly operational audits. Gain deep insights into attendance patterns and resource efficiency.",
    icon: "FiBarChart",
    status: "pending"
  }
];

export default function HowItWorks() {
  const [activeStep, setActiveStep] = useState(1);

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-chalk">Product Workflow</h1>
          <p className="text-slate-400 mt-2">A step-by-step guide to the EduOps operational lifecycle.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Step Selector */}
        <div className="lg:col-span-1 space-y-3">
          {steps.map((step) => (
            <button
              key={step.id}
              onClick={() => setActiveStep(step.id)}
              className={`w-full text-left p-4 rounded-xl border transition-all duration-300 flex items-center gap-4 group
                ${activeStep === step.id 
                  ? 'bg-blue-500/10 border-blue-500/30 ring-1 ring-blue-500/20' 
                  : 'bg-slate-900/40 border-slate-800 hover:border-slate-700'}
              `}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors
                ${activeStep === step.id ? 'bg-principal text-white' : 'bg-slate-800 text-slate-500 group-hover:text-slate-300'}
              `}>
                <SafeIcon icon={step.icon} className="text-lg" />
              </div>
              <div>
                <p className={`text-xs font-mono mb-0.5 ${activeStep === step.id ? 'text-principal' : 'text-slate-500'}`}>STEP 0{step.id}</p>
                <p className={`font-semibold ${activeStep === step.id ? 'text-chalk' : 'text-slate-400 group-hover:text-slate-200'}`}>{step.title}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Preview Panel */}
        <div className="lg:col-span-2">
          <div className="glass-panel rounded-2xl p-8 h-full min-h-[400px] flex flex-col items-center justify-center text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4">
              <div className="flex items-center gap-2 px-3 py-1 bg-slate-800/50 rounded-full border border-slate-700/50">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest">Simulator Live</span>
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="max-w-md"
              >
                <div className="w-20 h-20 bg-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-blue-500/30 text-principal shadow-lg shadow-blue-500/10">
                  <SafeIcon icon={steps[activeStep - 1].icon} className="text-4xl" />
                </div>
                <h2 className="text-2xl font-bold text-chalk mb-4">{steps[activeStep - 1].title}</h2>
                <p className="text-slate-400 leading-relaxed mb-8">{steps[activeStep - 1].desc}</p>
                
                <div className="p-6 rounded-2xl bg-slate-800/30 border border-slate-700/50 text-left">
                  <p className="text-xs font-mono text-slate-500 uppercase mb-3 tracking-widest">Simulated State</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Module Status:</span>
                    <span className={`text-xs font-bold px-2 py-1 rounded-full uppercase
                      ${steps[activeStep-1].status === 'ready' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 
                        steps[activeStep-1].status === 'active' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 
                        'bg-slate-700/50 text-slate-500'}
                    `}>{steps[activeStep-1].status}</span>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-700/50">
                    <button className="w-full bg-principal text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-500 transition-colors">
                      Demo This Routine
                    </button>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}